// src/components/SpyTicker/SpyTicker.tsx

import React, { useEffect, useState, useCallback } from 'react';
import { alpacaService, SymbolSnapshot } from '../../services/AlpacaService';
import { useAuth } from '../../context/AuthContext';
import styles from './SpyTicker.module.css';

const SYMBOLS = ['SPY', 'QQQ', 'DIA', 'IWM', 'GLD', 'XLE', 'VIX'];
const POLL_MS = 5_000;

interface MarketSession {
  session: 'pre_market' | 'market' | 'after_hours' | 'closed';
  label: string;
  mins_to_open?: number;
  mins_to_close?: number;
}

function formatCountdown(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

const SESSION_ICONS: Record<string, string> = {
  pre_market: '🌅',
  market: '🟢',
  after_hours: '🌙',
  closed: '🔴',
};

const SpyTicker: React.FC = () => {
  const { user } = useAuth();
  const [snapshots, setSnapshots] = useState<Record<string, SymbolSnapshot>>({});
  const [session, setSession] = useState<MarketSession | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await alpacaService.getSnapshot(SYMBOLS.join(','));
      setSnapshots(res.snapshots);
      if ((res as any).market_session) {
        setSession((res as any).market_session);
      }
    } catch {
      // silently fail — ticker is non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchData();
    const id = setInterval(fetchData, POLL_MS);
    return () => clearInterval(id);
  }, [user, fetchData]);

  if (!user || loading || Object.keys(snapshots).length === 0) return null;

  const countdown = session?.mins_to_open ?? session?.mins_to_close;
  const countdownLabel = session?.mins_to_open != null ? 'to open' : 'to close';

  return (
    <div className={styles.ticker}>
      {/* Market session badge */}
      {session && (
        <div className={`${styles.sessionBadge} ${styles[session.session] || ''}`}>
          <span className={styles.sessionIcon}>{SESSION_ICONS[session.session]}</span>
          <span className={styles.sessionLabel}>{session.label}</span>
          {countdown != null && countdown > 0 && (
            <span className={styles.sessionCountdown}>
              {formatCountdown(countdown)} {countdownLabel}
            </span>
          )}
        </div>
      )}

      {/* Price track */}
      <div className={styles.track}>
        {SYMBOLS.map((sym) => {
          const s = snapshots[sym];
          if (!s) return null;
          const isUp = s.change >= 0;
          return (
            <div key={sym} className={styles.item}>
              <span className={styles.symbol}>{sym}</span>
              <span className={styles.price}>${s.price.toFixed(2)}</span>
              <span className={`${styles.change} ${isUp ? styles.up : styles.down}`}>
                {isUp ? '+' : ''}
                {s.change.toFixed(2)} ({isUp ? '+' : ''}
                {s.change_pct.toFixed(2)}%)
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SpyTicker;
